/**
 * Cursed Relic — Plan 4b Task 5.1 (cast)
 *
 * Cursed Relic locks a target out of item use and dropped map-key pickup for
 * their next two completed own turns. Death clears the lockout.
 *
 * Stone Skin blocks valid hostile curse application.
 *
 * Self-registers via addItem(cursedRelic) at module load.
 */
import type { ActiveCursedRelic, GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { addItem } from './registry';
import { tryBlockWithStoneSkin } from '../stone-skin-block';
import { cursedRelicAppliedTurnKey } from '../cursed-relic-lockout';

export const cursedRelic: Item = {
  id: 'cursed_relic',
  name: 'Cursed Relic',
  description:
    'Curse a target player for their next 2 turns. They cannot use items or collect dropped map keys. Clears on death.',
  targetType: 'player',
  category: 'armory',
  rarity: 'rare',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;
    const targetId = ctx.target.targetPlayerId;

    if (!targetId || targetId === casterId) return { success: false };
    const target = state.players.get(targetId);
    if (!target) return { success: false };
    if (target.isDead) return { success: false };

    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'cursed_relic',
      targetId,
    });

    if (tryBlockWithStoneSkin(state, targetId, { actorId: casterId, sourceId: 'cursed_relic' })) {
      return { success: true, blockedByStoneSkin: true, endsTurn: false };
    }

    const turnsRemaining = 2;
    const appliedTurnKey = cursedRelicAppliedTurnKey(state, targetId);
    let refreshedExistingRelic = false;
    const refreshedRelics: ActiveCursedRelic[] = [];
    for (const relic of state.activeCursedRelics) {
      if (relic.victimId !== targetId) {
        refreshedRelics.push(relic);
      } else if (!refreshedExistingRelic) {
        refreshedRelics.push({ ...relic, casterId, turnsRemaining, appliedTurnKey });
        refreshedExistingRelic = true;
      }
    }
    if (!refreshedExistingRelic) {
      refreshedRelics.push({
        victimId: targetId,
        casterId,
        turnsRemaining,
        appliedTurnKey,
      });
    }
    state.activeCursedRelics = refreshedRelics;
    target.cursedRelicTurnsRemaining = turnsRemaining;

    appendEvent(state, {
      type: 'item_dot_applied',
      playerId: casterId,
      casterId,
      itemId: 'cursed_relic',
      victimId: targetId,
      turnsRemaining,
    });

    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(cursedRelic);
